import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Clock, Search, Home } from 'lucide-react';
import { Button } from '../ui/button';
import Logo from '../Logo/Logo';

const ExpiredPostView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <motion.div
      className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      role="main"
      aria-labelledby="expired-post-title"
    >
      <div className="max-w-md w-full text-center space-y-6">
        <motion.div
          className="flex flex-col items-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
        >
          <div className="relative">
            <Logo
              className="w-28 h-28 sm:w-32 sm:h-32 drop-shadow"
              tmClassName="-top-px right-1 text-[0.45rem]"
            />
            <span
              className="absolute -bottom-1 -right-1 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-amber-100 ring-4 ring-background flex items-center justify-center"
              aria-hidden="true"
            >
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-700" />
            </span>
          </div>
        </motion.div>

        <motion.h1
          id="expired-post-title"
          className="text-2xl sm:text-3xl font-bold text-foreground"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {t('expired_post_title', 'This post has expired')}
        </motion.h1>

        <motion.p
          className="text-sm sm:text-base text-muted-foreground leading-relaxed"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {t(
            'expired_post_body',
            "The post you're looking for is no longer active. The owner may have found their pet or the listing has timed out. Try one of the options below to keep looking.",
          )}
        </motion.p>

        <motion.div
          className="flex flex-col gap-3 pt-2"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            onClick={() => navigate('/')}
            className="h-11 px-6 w-full flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" aria-hidden="true" />
            {t('expired_post_home_cta', 'Go to homepage')}
          </Button>

          <Button
            variant="outline"
            onClick={() => navigate('/ai-search')}
            className="h-11 px-6 w-full flex items-center justify-center gap-2"
          >
            <Search className="w-4 h-4" aria-hidden="true" />
            {t('expired_post_search_cta', 'Search the app')}
          </Button>

        </motion.div>
      </div>
    </motion.div>
  );
};

export default memo(ExpiredPostView);
